import { useCallback, useContext, useEffect, useRef } from 'react'
import * as React from 'react'
import styled from 'styled-components'
import clsx from 'clsx'
import Caret from 'components/Common/Caret'
import Clickable from 'components/Common/Clickable'
import { rem } from 'polished'
import { OfferPageActions } from 'contexts/OfferPage/offerPageStateReducer'
import useOfferPageDispatch from 'hooks/OfferPage/useOfferPageDispatch'
import { getDurationOptions, isAvailableForPurchase } from 'selectors/offerPage/offerPageSelectors'
import { withOfferPageAndReduxState } from 'contexts/OfferPage/withOfferPageState'
import BodyText from 'LuxKit/Typography/BodyText'
import OfferPageStateTrackingContext from 'contexts/OfferPage/offerPageStateTrackingContext'
import CardCarousel from 'LuxKit/Carousel/CardCarousel'
import { packageOptionsClick } from 'analytics/packageDetails'
import { addGTMEvent } from 'api/googleTagManager'
import { mediaQueryUp } from 'components/utils/breakpoint'
import SolidFireIcon from 'LuxKit/Icons/solid/SolidFireIcon'
import BodyTextBlock from 'LuxKit/TextBlocks/BodyTextBlock'
import { isLPCTacticalEnabled } from 'lib/offer/offerUtils'
import Group from 'components/utils/Group'
import { mediaHoverable } from 'lib/theme/mediaQueries'
import { OFFER_TYPE_HOTEL } from 'constants/offer'
import { preventRenderWithOpenFilter } from 'lib/offer/offerPageUtil'
import { useIntl } from 'react-intl'
import {
  getElementLogicalInlineStartOffset,
  getLogicalScrollLeft,
  scrollToLogicalPosition,
} from 'LuxKit/Carousel/utils'
import type { OfferDurationOption } from 'lib/offer/offerDurationTypes'
import { REBRAND_PALETTE_CLASS } from 'lib/theme/paletteVersion'
import { DURATION_CAROUSEL_PROPS } from './durationCarouselLayout'
import { durationTabRebrandCaretStyles, durationTabRebrandOptionStyles } from './durationTabRebrandStyles'

const DurationFromText = styled(BodyText)`
  transition: color 0.2s;
`

const DurationCaret = styled(Caret)`
  position: absolute;
  bottom: 0;
  inset-inline-start: 50%;
  transform: translateY(100%) translateX(-50%);
  opacity: 0;
  transition: opactiy 0.2s, border-color 0.2s;
  border-top-color: ${props => props.theme.palette.brand.primary.normal};
  border-width: ${rem(8)};

  [dir='rtl'] & {
    transform: translateY(100%) translateX(50%);
  }

  &.visible {
    opacity: 1;
  }

  ${durationTabRebrandCaretStyles}
`

const DurationOption = styled(Clickable)`
  background-color: ${props => props.theme.palette.neutral.surface.normalBackground};
  transition: color 0.2s, background-color 0.2s;
  box-shadow: ${props => props.theme.shadow.bottom.small};
  position: relative;
  min-width: ${rem(120)};
  align-self: flex-end;
  border: 1px solid ${(props) => props.theme.palette.neutral.surface.lightForeground};

  &.selected {
    color: ${props => props.theme.palette.neutral.surface.darkContrast};
    background-color: ${props => props.theme.palette.brand.primary.normal};

    ${mediaHoverable} {
      &:hover {
        color: ${props => props.theme.palette.neutral.surface.darkContrast};
        background-color: ${props => props.theme.palette.brand.primary.hover};

        ${DurationCaret} {
          border-top-color: ${props => props.theme.palette.brand.primary.hover};
        }
      }
    }
    &:focus {
      color: ${props => props.theme.palette.neutral.surface.darkContrast};
      background-color: ${props => props.theme.palette.brand.primary.hover};

      ${DurationCaret} {
        border-top-color: ${props => props.theme.palette.brand.primary.hover};
      }
    }
  }

  &:hover, &:focus {
    color: ${props => props.theme.palette.neutral.surface.darkContrast};
    background-color: ${props => props.theme.palette.brand.primary.normal};

    ${DurationFromText} {
      color: ${props => props.theme.palette.neutral.surface.darkContrast};
    }
  }

  ${durationTabRebrandOptionStyles(DurationCaret)}

  body.${REBRAND_PALETTE_CLASS} &:hover,
  body.${REBRAND_PALETTE_CLASS} &:focus {
    ${DurationFromText} {
      color: var(--palette-highlight-primary-dark-contrast);
    }
  }
`

const DurationOptionContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: ${rem(44)};
  padding: ${rem(4)} ${rem(12)};
`

const BonusInclusionTag = styled(Group)`
  min-width: 0;
  overflow: hidden;
  padding: ${rem(4)};
  background-color: ${props => props.theme.palette.highlight.primary.normalBackground};
`

const BonusInclusionTagText = styled(BodyTextBlock)`
  min-width: 0;

  > * {
    min-width: 0;
  }
`

const OfferDurations = styled.div`
  ${mediaQueryUp.desktop} {
    &.centered {
      display: flex;
      justify-content: center;
    }
  }
`

interface Props {
  durationOptions: Array<OfferDurationOption>
  durationOptionsOverride?: Array<OfferDurationOption>
  selectedDuration: number
  onBeforeDurationClick?: (duration: number) => boolean
  className?: string
  isFlash: boolean
  availableForPurchase?: boolean
  availableForPurchaseOverride?: boolean
}

function OfferDurationTabs(props: Props) {
  const {
    durationOptions,
    selectedDuration,
    onBeforeDurationClick,
    className,
    isFlash,
    availableForPurchase,
  } = props
  const tabRefs = useRef<Record<string, HTMLButtonElement>>({})
  const intl = useIntl()

  const { trackEvent } = useContext(OfferPageStateTrackingContext)
  const offerPageDispatch = useOfferPageDispatch()

  const onDurationClick = useCallback((e: React.MouseEvent<HTMLButtonElement>) => {
    const duration = parseInt(e.currentTarget.dataset.duration!, 10)
    if (onBeforeDurationClick && !onBeforeDurationClick(duration)) return
    offerPageDispatch({ type: OfferPageActions.setDuration, duration })
    addGTMEvent(packageOptionsClick(duration))
    trackEvent('DURATION_PRESSED')
  }, [offerPageDispatch, onBeforeDurationClick, trackEvent])

  useEffect(() => {
    const tabElement = tabRefs.current[selectedDuration]
    const parentElement = tabElement?.parentElement
    // ensure the selected duration is always visible in the carousel
    if (tabElement && parentElement) {
      const startElementPx = getElementLogicalInlineStartOffset(tabElement, parentElement)
      const endElementPx = startElementPx + tabElement.offsetWidth
      const startParentPx = getLogicalScrollLeft(parentElement)
      const endParentPx = parentElement.offsetWidth + startParentPx

      // check if our elements bounds are outside of the currently visible bounds on the scroll parent
      const isNotVisible = startElementPx > endParentPx || endElementPx < startParentPx
      if (isNotVisible) {
        scrollToLogicalPosition(parentElement, { left: startElementPx })
      }
    }
  }, [selectedDuration])

  return (<OfferDurations className={clsx(className, { centered: durationOptions.length < 8 })}>
    <CardCarousel
      gap={12}
      {...DURATION_CAROUSEL_PROPS}
    >
      {durationOptions.map((option, index) => {
        const selected = selectedDuration === option.duration
        const hasTactical = isLPCTacticalEnabled() && option?.hasTactical
        const hasBonus = option?.hasBonusInclusions
        const hasPromotion = option?.hasPromotion

        return <DurationOption
          key={index}
          className={clsx({ selected })}
          data-testid={`nights-${option.duration}`}
          data-duration={option.duration}
          onClick={onDurationClick}
          ref={(ref) => {
            if (ref instanceof HTMLButtonElement) {
              tabRefs.current[option.duration] = ref
            }
          }}
        >
          {!isFlash && (hasTactical || hasBonus || hasPromotion) && <BonusInclusionTag direction="horizontal" horizontalAlign="center" fullWidth>
            <BonusInclusionTagText
              startIcon={hasTactical ? <SolidFireIcon /> : undefined}
              variant="small"
              colour="normal-contrast"
              align="center"
              format="propercase"
            >
              {!hasPromotion && intl.formatMessage({
                id: 'xStPetV0hCpAS9eII8Af',
                defaultMessage: 'extra inclusions',
              })}
              {hasPromotion && intl.formatMessage({
                id: 'expGV8AK02RetvDh4pe7',
                defaultMessage: 'Special',
              })}
            </BonusInclusionTagText>
          </BonusInclusionTag>}
          <DurationOptionContent>
            <BodyText variant="medium" weight="bold" wrap="no-wrap">
              {intl.formatMessage(
                {
                  id: 'PGhiDxQHn4Nqxoxcm8Dl',
                  defaultMessage: '{count, plural, one {# night} other {# nights}}',
                },
                { count: option.duration },
              )}
            </BodyText>
            <DurationFromText variant="small" weight="bold" colour={!selected ? 'primary' : undefined}>
              {!availableForPurchase && intl.formatMessage({
                id: 's1L06bF7ntdvjoMC0aYD',
                defaultMessage: 'SOLD OUT',
              })}
            </DurationFromText>
            <DurationCaret className={clsx({ visible: selected })} />
          </DurationOptionContent>
        </DurationOption>
      })}
    </CardCarousel>
  </OfferDurations>)
}

export default withOfferPageAndReduxState((state, appState, ownProps) => ({
  selectedDuration: state.duration,
  durationOptions: ownProps.durationOptionsOverride ?? getDurationOptions(state, appState),
  isFlash: state.offer?.type === OFFER_TYPE_HOTEL || state.property?.promotion?.type === 'dynamicFlash',
  availableForPurchase: ownProps.availableForPurchaseOverride ?? isAvailableForPurchase(state, appState),
  open: !!state.activeFilter,
}), OfferDurationTabs, preventRenderWithOpenFilter)
